import { useState, useEffect, useCallback } from 'react'
import { cyberpunkTheme } from '../../styles/theme'
import { soundManager } from '../../utils/sounds'

const STYLES = {
  success: { bg: 'rgba(0,245,255,0.1)',   border: 'rgba(0,245,255,0.3)',  icon: '►', color: '#00f5ff' },
  error:   { bg: 'rgba(255,0,64,0.1)',    border: 'rgba(255,0,64,0.3)',   icon: '✕', color: '#ff0040' },
  warning: { bg: 'rgba(255,149,0,0.1)',  border: 'rgba(255,149,0,0.3)', icon: '!', color: '#ff9500' },
  info:    { bg: 'rgba(0,245,255,0.08)',  border: 'rgba(0,245,255,0.25)', icon: '?', color: '#00f5ff' },
  loading: { bg: 'rgba(168,85,247,0.1)', border: 'rgba(168,85,247,0.3)', icon: '~', color: '#a855f7' },
}

let _toastId = 0
let _addToast = null

export function toast(message, type = 'info', duration = 4000) {
  if (_addToast) _addToast({ id: ++_toastId, message, type, duration })
}
toast.success = (msg, dur = 4000) => toast(msg, 'success', dur)
toast.error   = (msg, dur = 5000) => toast(msg, 'error',   dur)
toast.warning = (msg, dur = 4000) => toast(msg, 'warning', dur)
toast.info    = (msg, dur = 4000) => toast(msg, 'info',    dur)
toast.loading = (msg)             => toast(msg, 'loading', 99999)

function ToastItem({ t, onRemove }) {
  const [visible, setVisible] = useState(false)
  const s = STYLES[t.type] || STYLES.info

  useEffect(() => {
    const show = setTimeout(() => setVisible(true), 10)
    if (t.duration < 99999) {
      const hide = setTimeout(() => {
        setVisible(false)
        setTimeout(() => onRemove(t.id), 300)
      }, t.duration)
      return () => { clearTimeout(show); clearTimeout(hide) }
    }
    return () => clearTimeout(show)
  }, [])

  return (
    <div
      onClick={() => { setVisible(false); setTimeout(() => onRemove(t.id), 300) }}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
        padding: 'clamp(0.5rem, 2vw, 0.75rem) clamp(0.65rem, 2vw, 1rem)',
        background: s.bg,
        border: `2px solid ${s.border}`,
        borderRadius: '4px',
        cursor: 'pointer',
        width: 'min(340px, calc(100vw - 1.5rem))',
        maxWidth: '95vw',
        boxShadow: `0 4px 20px rgba(0,0,0,0.5), 0 0 12px ${s.border}`,
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(-16px) scale(0.95)',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={{
        fontSize: '0.7rem', flexShrink: 0, marginTop: '1px',
        color: s.color,
        textShadow: `0 0 6px ${s.border}`,
        fontFamily: cyberpunkTheme.fonts.display,
      }}>
        {s.icon}
      </span>
      <p style={{
        color: '#e0e0f0', fontSize: 'clamp(0.6rem, 2.5vw, 0.7rem)', margin: 0,
        lineHeight: 1.8, wordBreak: 'break-word', flex: 1,
        fontFamily: cyberpunkTheme.fonts.body,
      }}>
        {t.message}
      </p>
    </div>
  )
}

export function ToastContainer() {
  const [toasts, setToasts] = useState([])

  const addToast = useCallback((t) => {
    setToasts(prev => [t, ...prev].slice(0, 4))
    // Play subtle audio cue per toast type
    if (t.type === 'error') soundManager.playError()
    else if (t.type === 'success' || t.type === 'info' || t.type === 'warning') soundManager.playNotification()
  }, [])

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  useEffect(() => {
    _addToast = addToast
    return () => { _addToast = null }
  }, [addToast])

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      style={{
      position: 'fixed',
      top: 'max(0.75rem, env(safe-area-inset-top, 0.75rem))',
      left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem',
      pointerEvents: 'none',
      width: 'min(340px, calc(100vw - 1rem))',
      padding: '0 0.5rem', boxSizing: 'border-box',
    }}>
      {toasts.map(t => (
        <div key={t.id} style={{ pointerEvents: 'auto' }}>
          <ToastItem t={t} onRemove={removeToast} />
        </div>
      ))}
    </div>
  )
}
